<template>
  <div class="bot-tab">
    <div class="kpi-row">
      <div class="kpi-card bot">
        <div class="kpi-label">Победы бота</div>
        <div class="kpi-value">{{ fmtNum(botWins) }}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Без ставки (гарантии)</div>
        <div class="kpi-value">{{ fmtNum(noBidTrips) }}</div>
        <div class="kpi-sub">победа без рассчитанной ставки</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Доля бота</div>
        <div class="kpi-value">{{ botSharePct }}</div>
        <div class="kpi-sub">{{ fmtNum(botWins + humanWins) }} рейсов всего</div>
      </div>
    </div>

    <div class="kpi-row" v-if="auctionTotals">
      <div class="kpi-card bot">
        <div class="kpi-label">Ставок ботом (аукционы)</div>
        <div class="kpi-value">{{ fmtNum(auctionTotals.bids_bot) }}</div>
        <div class="kpi-sub">из {{ fmtNum(auctionTotals.auctions_total) }} аукционов</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Проиграно ботом</div>
        <div class="kpi-value loss">{{ fmtNum(auctionTotals.lost_bot) }}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Выиграно ботом</div>
        <div class="kpi-value">{{ fmtNum(auctionTotals.won_bot) }}</div>
        <div class="kpi-sub">идёт торгов: {{ fmtNum(auctionTotals.play_bot) }}</div>
      </div>
    </div>

    <div class="chart-block" v-if="auctionRows.length">
      <div class="chart-title">Аукционы по дням по категориям</div>
      <VChart class="chart" :option="auctionDayOption" autoresize />
    </div>
    <div class="chart-block v-else">
      <div class="chart-title">Аукционы по дням</div>
      <div v-if="isLoading" class="chart-empty">Загрузка…</div>
      <div v-else class="chart-empty">Нет данных по выбранным фильтрам</div>
    </div>

    <div class="table-scroll">
      <table class="stat-table" v-if="summaryRows.length">
        <thead>
          <tr>
            <th>Источник победы</th>
            <th class="num">Рейсов</th>
            <th class="num">Закрыто</th>
            <th class="num">Ср. маржа</th>
            <th class="num">Ср. рентаб.</th>
            <th class="num">Ср. комиссия</th>
            <th class="num">Комиссия</th>
            <th class="num">Ср. поиск, ч.</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in orderedSummary" :key="row.win_source">
            <td><span class="src-pill" :class="srcClass(row.win_source)">{{ row.win_source }}</span></td>
            <td class="num strong">{{ fmtNum(row.trips) }}</td>
            <td class="num">{{ fmtNum(row.closed) }}</td>
            <td class="num">{{ fmtPct(row.avg_margin_pct) }}</td>
            <td class="num">{{ fmtPct(row.avg_profitability_pct) }}</td>
            <td class="num">{{ fmtMoney(row.avg_commission) }}</td>
            <td class="num strong">{{ fmtMoney(row.total_commission) }}</td>
            <td class="num">{{ fmtHoursNum(row.avg_search_sec) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!isLoading" class="empty-table">Нет данных по выбранным фильтрам</div>
    </div>
    <div class="footnote">
      Победа бота = автор выигравшей ставки (cost.last.from_bot), активность
      задачи автоторга победой не считается. «Без ставки» — победы без
      рассчитанной цены (гарантийные потоки без торгов).
      Маржа/комиссия по спот-рейсам часто пусты — у них нет пары в ERP с тарифами.
      Блок «аукционы» — зерно pure_bidding (WON/LOST/PLAY по дню открытия):
      каждый аукцион = отдельный док, пере-публикации рейса считаются отдельно.
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import VChart from '@/components/charts/echarts';
import { fmtDate, fmtHoursNum, fmtMoney, fmtNum, fmtPct } from '../format';

const SRC_ORDER = ['Бот', 'Человек', 'Без ставки'];

export default {
  name: 'BotTab',
  components: { VChart },
  props: {
    summaryRows: { type: Array, default: () => [] },
    auctionRows: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
  },
  setup(props) {
    const summaryBy = (source) =>
      props.summaryRows.find((row) => row.win_source === source);

    const botWins = computed(() => Number((summaryBy('Бот') || {}).trips) || 0);
    const humanWins = computed(() => Number((summaryBy('Человек') || {}).trips) || 0);
    const noBidTrips = computed(
      () => Number((summaryBy('Без ставки') || {}).trips) || 0
    );
    const botSharePct = computed(() => {
      const total = botWins.value + humanWins.value;
      if (!total) return '—';
      return `${fmtNum((botWins.value / total) * 100, 1)}%`;
    });

    const orderedSummary = computed(() =>
      SRC_ORDER.map((src) => summaryBy(src)).filter(Boolean)
    );

    const AUCTION_KEYS = [
      'auctions_total',
      'bids_bot',
      'bids_human',
      'no_bid',
      'won_bot',
      'won_human',
      'lost_bot',
      'lost_human',
      'play_bot',
      'play_human',
    ];
    const auctionTotals = computed(() => {
      if (!props.auctionRows.length) return null;
      const totals = {};
      AUCTION_KEYS.forEach((key) => {
        totals[key] = props.auctionRows.reduce(
          (acc, row) => acc + (Number(row[key]) || 0),
          0
        );
      });
      return totals;
    });

    const auctionDayOption = computed(() => ({
      grid: { left: 8, right: 16, top: 32, bottom: 8, containLabel: true },
      legend: {
        top: 0,
        textStyle: { fontSize: 11, color: '#667085' },
      },
      tooltip: { trigger: 'axis', confine: true },
      xAxis: {
        type: 'category',
        data: props.auctionRows.map((row) => fmtDate(row.day)),
        axisLabel: { rotate: 45, fontSize: 10 },
      },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        {
          name: 'Не рассчитанные',
          type: 'bar',
          stack: 'auctions',
          barMaxWidth: 28,
          data: props.auctionRows.map((row) => Number(row.no_bid) || 0),
          itemStyle: { color: '#d0d5dd' },
        },
        {
          name: 'Рассчитано без ставки бота',
          type: 'bar',
          stack: 'auctions',
          barMaxWidth: 28,
          data: props.auctionRows.map((row) => Number(row.bids_human) || 0),
          itemStyle: { color: '#3b82f6' },
        },
        {
          name: 'Выиграно ботом',
          type: 'bar',
          stack: 'auctions',
          barMaxWidth: 28,
          data: props.auctionRows.map((row) => Number(row.won_bot) || 0),
          itemStyle: { color: '#12b76a' },
        },
        {
          name: 'Проиграно ботом',
          type: 'bar',
          stack: 'auctions',
          barMaxWidth: 28,
          data: props.auctionRows.map((row) => Number(row.lost_bot) || 0),
          itemStyle: { color: '#f04438' },
        },
      ],
    }));

    const srcClass = (source) => {
      if (source === 'Бот') return 'src-bot';
      if (source === 'Человек') return 'src-human';
      if (source === 'Без ставки') return 'src-nobid';
      return 'src-none';
    };

    return {
      botWins,
      humanWins,
      noBidTrips,
      botSharePct,
      orderedSummary,
      auctionTotals,
      auctionDayOption,
      srcClass,
      fmtHoursNum,
      fmtMoney,
      fmtNum,
      fmtPct,
    };
  },
};
</script>

<style scoped>
.bot-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.kpi-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 12px 16px;
}

.kpi-card.bot {
  border-color: #e4d7ff;
  background: #f9f7ff;
}

.kpi-card.human {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.kpi-label {
  font-size: 12px;
  color: #667085;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1d2939;
  font-variant-numeric: tabular-nums;
}

.kpi-card.bot .kpi-value {
  color: #5925dc;
}

.kpi-card.human .kpi-value {
  color: #1d4ed8;
}

.kpi-value.loss {
  color: #d92d20;
}

.kpi-sub {
  font-size: 12px;
  color: #98a2b3;
  margin-top: 2px;
}

.chart-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
}

.chart {
  width: 100%;
  height: 320px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
  font-size: 14px;
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stat-table th {
  background: #f8fafc;
  color: #667085;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.stat-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  color: #1d2939;
  white-space: nowrap;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.strong {
  font-weight: 600;
}

.src-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
}

.src-bot {
  background: #f4f0ff;
  color: #5925dc;
}

.src-human {
  background: #eff6ff;
  color: #1d4ed8;
}

.src-nobid {
  background: #f2f4f7;
  color: #475467;
}

.src-none {
  background: #f2f4f7;
  color: #475467;
}

.footnote {
  font-size: 12px;
  color: #98a2b3;
}

.empty-table {
  padding: 40px;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
